import { type ReactElement, type ReactNode, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import {
  RadioSelector,
  RadioSelectorOption,
  type RadioSelectorOptionProps,
  H5,
  H6,
} from "@sellar-io/components";
import {
  InputContainer,
  InputOption,
  InputSelect,
} from "@sellar-io/components";
import { generateImgixUrl, imageUrls } from "@sellar-io/components";
import { Link } from "../../settings/integrationSettings/Link";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { DX_CUTOFF_COPY } from "../../../constants/dxCutoff.shared";

interface ConfirmOrderSellarShippingRadioSelectorProps {
  value: string;
  required?: boolean;
  onChange: (value: string) => void;
  order?: OrderGetResponseType;
  dispatchPlannedAt?: string | null;
  upsAlert?: ReactNode | null;
}

export const ConfirmOrderSellarShippingRadioSelector = (
  props: ConfirmOrderSellarShippingRadioSelectorProps,
) => {
  const { value } = props;
  const [dxPreference, setDxPreference] = useLocalStorage<"dx" | "dxDropOff">(
    "dxSelectionPreference",
    "dx",
  );
  const [dxValue, setDxValue] = useState<"dx" | "dxDropOff">(
    value === "dx" || value === "dxDropOff" ? value : dxPreference,
  );
  const { isActive } = useRemoteFeatureFlags();

  const isApcFulfillmentActive = isActive("enableApcFulfillmentType");
  const isDxFulfillmentType = value === "dx" || value === "dxDropOff";
  const [previousValue, setPreviousValue] = useState(value);
  if (value !== previousValue) {
    setPreviousValue(value);
    if (isDxFulfillmentType) {
      setDxValue(value);
    }
  }

  const fulfillmentOptions = [
    ...(isApcFulfillmentActive
      ? [
          <RadioSelectorOption key="apc" value={"apc"}>
            <APCInformation />
          </RadioSelectorOption>,
        ]
      : []),
    <RadioSelectorOption key="ups" value={"ups"}>
      <div className="flex flex-col gap-4">
        {props.upsAlert}
        <UPSInformation />
      </div>
    </RadioSelectorOption>,
    <RadioSelectorOption key={dxValue} value={dxValue}>
      <DXInformation
        dxValue={dxValue}
        onDxValueChange={(fulfillment) => {
          setDxValue(fulfillment);
          setDxPreference(fulfillment);
          props.onChange(fulfillment);
        }}
      />
    </RadioSelectorOption>,
    <RadioSelectorOption key="courier" value={"courier"}>
      <div className="flex flex-col gap-2">
        <H5>I'll arrange my own courier</H5>
        <H6>You will be responsible for arranging your own courier.</H6>
      </div>
    </RadioSelectorOption>,
  ] satisfies Array<ReactElement<RadioSelectorOptionProps>>;

  return (
    <div className="flex flex-col gap-4">
      <RadioSelector
        value={value}
        onChange={(e) => props.onChange(e.currentTarget.value)}
        required={true}
      >
        {fulfillmentOptions}
      </RadioSelector>
    </div>
  );
};

export const UPSInformation = () => {
  const { isActive } = useRemoteFeatureFlags();
  const isApcFulfillmentActive = isActive("enableApcFulfillmentType");

  return (
    <div className="flex flex-row justify-between">
      <div className="flex flex-col gap-2">
        <H5>Courier - UPS</H5>
        {isApcFulfillmentActive ? (
          <div className="flex flex-col">
            <H6>Lowest cost</H6>
            <H6>1-2 day economy delivery</H6>
            <H6>Ships items up to 25kg</H6>
          </div>
        ) : (
          <div className="flex flex-col">
            <H6>1-2 day delivery</H6>
            <H6>Same day and next day collection available</H6>
            <H6>Ships only fully boxed products under 25kg</H6>
            <H6>
              Tracking, customer delivery notifications and proof of delivery
            </H6>
            <H6>
              Invoice value insurance up to £80 per boxed item - no excess
            </H6>
          </div>
        )}
        <Link
          to={
            "https://intercom.help/sellario/en/articles/9739738-supplier-guide-sellar-shipping-item-rates-courier-charges"
          }
          target="_blank"
          icon={"OPEN_IN_NEW"}
        >
          View Pricing & Info
        </Link>
      </div>
      <img
        alt={"ups"}
        src={generateImgixUrl(imageUrls.upsLogo, {
          compress: true,
          width: 48,
        })}
        className={"h-12 w-12"}
      />
    </div>
  );
};

export const APCInformation = () => {
  return (
    <div className="flex flex-row justify-between">
      <div className="flex flex-col gap-2">
        <H5>Courier - APC</H5>
        <div className="flex flex-col">
          <H6>Liquid specialist delivery service</H6>
          <H6>Next day delivery</H6>
          <H6>Ships items up to 30kg</H6>
          <H6 className="font-semibold">Recommended</H6>
        </div>
        <Link
          to={
            "https://intercom.help/sellario/en/articles/12275512-supplier-guide-sellar-shipping-with-apc"
          }
          target="_blank"
          icon={"OPEN_IN_NEW"}
        >
          View Pricing & Info
        </Link>
      </div>
      <img alt={"apc"} src={imageUrls.apcLogo} className={"h-12 w-12"} />
    </div>
  );
};

export const DXInformation = ({
  dxValue,
  onDxValueChange,
}: {
  dxValue: "dx" | "dxDropOff";
  onDxValueChange: (fulfillment: "dx" | "dxDropOff") => void;
}) => {
  return (
    <div className="flex flex-row justify-between">
      <div className="flex flex-col gap-2">
        <H5>Courier - DX</H5>
        <div className="flex flex-col">
          <H6>Best for large & heavy orders</H6>
          <H6>Next day delivery</H6>
          <H6>Ideal for steel kegs & casks</H6>
        </div>
        <Link
          to={
            "https://intercom.help/sellario/en/articles/12709597-supplier-guide-dx-hub-shipping-and-prices"
          }
          target="_blank"
          icon={"OPEN_IN_NEW"}
        >
          View Pricing & Info
        </Link>
        <InputContainer>
          <InputSelect
            value={dxValue}
            onChange={(e) =>
              onDxValueChange(e.target.value as "dx" | "dxDropOff")
            }
          >
            <InputOption key={"dxDropOff"} value={"dxDropOff"}>
              I will drop off this order at my local DX drop off point / I am a
              DX drop off point
            </InputOption>
            <InputOption key={"dx"} value={"dx"}>
              I want DX to collect directly from me using their ad-hoc service
            </InputOption>
          </InputSelect>
        </InputContainer>
        <H6 className="text-zinc-500">{DX_CUTOFF_COPY[dxValue]}</H6>
      </div>
      <img alt={"dx"} src={imageUrls.dxLogo} className={"h-12 w-12"} />
    </div>
  );
};
